import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const abilityLadder = [
  { level: '第一层', label: '看见画面', desc: '识别PPT页、板书、视频播放、教师位置和课堂活动', icon: 'ri-eye-line' },
  { level: '第二层', label: '理解片段', desc: '判断属于导入、史料呈现、现实分析还是总结升华', icon: 'ri-mental-health-line' },
  { level: '第三层', label: '进入评价', desc: '画面与语音文本、课件内容和评价指标对应', icon: 'ri-scales-3-line' },
];

const caseA = {
  code: '本科06',
  title: '视觉结构正向案例',
  status: '已核',
  features: ['福建舰引出现实素材', '历史素材→现实数据→未来挑战', '侧边板书与课件三段对应', '专家侧头部、学生侧前部'],
  tag: '正向案例',
  tagColor: 'bg-primary-100/60 text-primary-700',
};

const caseB = {
  code: '本科07',
  title: '结构外显对照案例',
  status: '待补证据',
  features: ['课件结构和教学环节更显性', '模型更容易捕捉导入—讲授—总结', '用于检验视觉模型稳定划分能力', '正式上屏前补关键帧和原话'],
  tag: '对照案例',
  tagColor: 'bg-accent-100/60 text-accent-700',
};

export default function P27VisualUnderstanding({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <div ref={titleRef} className="text-center mb-5 md:mb-7">
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100/60 text-primary-700 text-[11px] font-body font-semibold">
              <i className="ri-scan-line text-xs" /> 技术点一
            </span>
          </div>
          <h2 className={`font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            长视频切片与<span className="text-primary-600">课堂视觉理解</span>
          </h2>
        </div>

        {/* 三级能力阶梯 */}
        <div className={`max-w-4xl mx-auto mb-6 transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="grid grid-cols-3 gap-3">
            {abilityLadder.map((step, i) => (
              <div key={step.level} className="p-4 rounded-2xl glass border-white/50 text-center hover:border-primary-200/40 transition-all duration-500" style={{ transitionDelay: `${200 + i * 120}ms` }}>
                <div className="w-10 h-10 mx-auto mb-2 rounded-xl glass-blue border-primary-200/40 flex items-center justify-center">
                  <i className={`${step.icon} text-base text-primary-600`} />
                </div>
                <p className="text-[10px] font-body font-semibold text-foreground-400">{step.level}</p>
                <p className="text-xs font-body font-bold text-foreground-800 mt-0.5">{step.label}</p>
                <p className="text-[11px] text-foreground-500 font-body mt-0.5 leading-tight">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 双案例对照 */}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 max-w-5xl mx-auto transition-all duration-1000 delay-400 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* 本科06 */}
          <div className="p-5 rounded-2xl glass-liquid border-primary-200/30">
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-sm font-bold text-foreground-800">{caseA.code} {caseA.title}</h3>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-body font-semibold ${caseA.tagColor}`}>{caseA.tag}</span>
              </div>
              <div className="flex items-center gap-1.5 mb-3">
                <i className="ri-verified-badge-fill text-accent-500 text-xs" />
                <span className="text-[11px] font-body text-accent-600 font-semibold">状态：{caseA.status}</span>
              </div>
              <ul className="space-y-1.5">
                {caseA.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[11px] text-foreground-600 font-body">
                    <i className="ri-check-line text-primary-500 text-xs mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-3 pt-3 border-t border-primary-100/50">
                <p className="text-[10px] text-foreground-400 font-body">
                  关键帧可还原"提问引入—历史素材—现实数据—未来挑战—总结升华"叙事路径
                </p>
              </div>
            </div>
          </div>

          {/* 本科07 */}
          <div className="p-5 rounded-2xl glass-liquid-gold border-accent-200/30">
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-sm font-bold text-foreground-800">{caseB.code} {caseB.title}</h3>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-body font-semibold ${caseB.tagColor}`}>{caseB.tag}</span>
              </div>
              <div className="flex items-center gap-1.5 mb-3">
                <i className="ri-time-line text-foreground-400 text-xs" />
                <span className="text-[11px] font-body text-foreground-500">状态：{caseB.status}</span>
              </div>
              <ul className="space-y-1.5">
                {caseB.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[11px] text-foreground-600 font-body">
                    <i className="ri-subtract-line text-accent-500 text-xs mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-3 pt-3 border-t border-accent-100/50">
                <p className="text-[10px] text-foreground-400 font-body">
                  用于说明：环节越清楚，视觉模型越容易稳定划分；内容切换靠口头过渡时视觉信号变弱
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 数据条 */}
        <div className={`mt-5 max-w-3xl mx-auto text-center transition-all duration-1000 delay-900 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl glass border-white/50">
            <i className="ri-bar-chart-line text-sm text-primary-500" />
            <span className="text-xs font-body text-foreground-500">
              专用感染力模型分数离散度约<span className="font-bold text-primary-700">10</span>，明显高于通用模型约<span className="font-bold">2.7</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}